import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { debug } from '@/utils/debug';
import { isUrlGloballyAuthorized } from '@/utils/urlUtils';
import { useAuthStore } from '@/store/authStore';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import apiClient from '@/services/api/client';
import { cn } from '@/utils/cn';

interface AdGroup {
  ad_group_id?: string;
  ads_group_id?: string;
  ad_group_name?: string;
  ads_group_name?: string;
}

interface CreateRSAWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  campaignName: string;
  adGroups: AdGroup[];
}

interface FormErrors {
  adGroup?: string;
  headlines?: string;
  descriptions?: string;
  finalUrl?: string;
}

// Form data for RSA
interface RSAFormData {
  headlines: string[];
  descriptions: string[];
  finalUrl: string;
  path1: string;
  path2: string;
}

// API types for RSA creation
interface CreateRSARequest {
  ad_group_id: string;
  headlines: string[];
  descriptions: string[];
  final_url: string;
  path1?: string;
  path2?: string;
}

interface CreateRSAResponse {
  success: boolean;
  ad_id: string;
  resource_name: string;
  message: string;
}

const wizardSteps = [
  { label: 'Ad Group', description: '' },
  { label: 'Search Ad', description: '' },
  { label: 'Review', description: '' },
];

export const CreateRSAWizard: React.FC<CreateRSAWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  campaignName,
  adGroups,
}) => {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Selected ad group
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string>('');

  // RSA form data
  const [formData, setFormData] = useState<RSAFormData>({
    headlines: ['', '', ''],
    descriptions: ['', ''],
    finalUrl: '',
    path1: '',
    path2: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Get selected ad group details
  const selectedAdGroup = adGroups.find(
    (ag) => (ag.ad_group_id || ag.ads_group_id) === selectedAdGroupId
  );
  const selectedAdGroupName = selectedAdGroup?.ad_group_name || selectedAdGroup?.ads_group_name || '';

  // Reset form when wizard opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
      setSubmitError(null);
      setSelectedAdGroupId('');
      setFormData({
        headlines: ['', '', ''],
        descriptions: ['', ''],
        finalUrl: '',
        path1: '',
        path2: '',
      });
    }
  }, [isOpen]);

  // Headline/Description handlers
  const handleHeadlineChange = (index: number, value: string) => {
    const updated = [...formData.headlines];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, headlines: updated }));
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...formData.descriptions];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, descriptions: updated }));
  };

  const addHeadline = () => {
    if (formData.headlines.length < 15) {
      setFormData((prev) => ({ ...prev, headlines: [...prev.headlines, ''] }));
    }
  };

  const removeHeadline = (index: number) => {
    if (formData.headlines.length > 3) {
      setFormData((prev) => ({
        ...prev,
        headlines: prev.headlines.filter((_, i) => i !== index),
      }));
    }
  };

  const addDescription = () => {
    if (formData.descriptions.length < 4) {
      setFormData((prev) => ({ ...prev, descriptions: [...prev.descriptions, ''] }));
    }
  };

  const removeDescription = (index: number) => {
    if (formData.descriptions.length > 2) {
      setFormData((prev) => ({
        ...prev,
        descriptions: prev.descriptions.filter((_, i) => i !== index),
      }));
    }
  };

  // Validation function for each step
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      // Validate ad group selection
      if (!selectedAdGroupId) {
        newErrors.adGroup = 'Please select an ad group';
      }
    }

    if (step === 2) {
      // Validate RSA fields
      if (!formData.finalUrl.trim()) {
        newErrors.finalUrl = 'Final URL is required';
      } else if (!formData.finalUrl.startsWith('http://') && !formData.finalUrl.startsWith('https://')) {
        newErrors.finalUrl = 'Final URL must start with http:// or https://';
      }

      const nonEmptyHeadlines = formData.headlines.filter((h) => h.trim().length > 0);
      if (nonEmptyHeadlines.length < 3) {
        newErrors.headlines = 'At least 3 headlines are required';
      } else if (formData.headlines.some((h) => h.trim().length > 0 && h.trim().length > 30)) {
        newErrors.headlines = 'Headlines must be 30 characters or less';
      }

      const nonEmptyDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
      if (nonEmptyDescriptions.length < 2) {
        newErrors.descriptions = 'At least 2 descriptions are required';
      } else if (formData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
        newErrors.descriptions = 'Descriptions must be 90 characters or less';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, wizardSteps.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build API request
      const request: CreateRSARequest = {
        ad_group_id: selectedAdGroupId,
        headlines: formData.headlines.filter((h) => h.trim().length > 0),
        descriptions: formData.descriptions.filter((d) => d.trim().length > 0),
        final_url: formData.finalUrl.trim(),
        path1: formData.path1.trim() || undefined,
        path2: formData.path2.trim() || undefined,
      };

      debug.log('CreateRSAWizard: Submitting RSA creation request', { request, campaignId, selectedAdGroupId });

      // Call the API to create the RSA
      const response = await apiClient.post<CreateRSAResponse>(
        `/campaigns/${campaignId}/ad-groups/${selectedAdGroupId}/ads/rsa`,
        request,
        { timeout: 60000 } // 1 minute timeout
      );

      debug.log('CreateRSAWizard: RSA created successfully', response.data);

      // Check if URL is globally authorized in existing config
      const existingConfig = memberUuid ? queryClient.getQueryData<AppConfiguration>(['app-config', memberUuid]) : null;
      const urlIsAuthorized = isUrlGloballyAuthorized(formData.finalUrl, existingConfig);
      debug.log('CreateRSAWizard: URL authorization check', { url: formData.finalUrl, urlIsAuthorized });

      // Build optimistic ad data
      const optimisticAd = {
        ad_id: response.data.ad_id || `optimistic_${Date.now()}`,
        ad_type: 'RESPONSIVE_SEARCH_AD',
        ad_name: formData.headlines[0] || 'New Search Ad',
        status: 'ENABLED' as const,
        headlines: formData.headlines.filter((h) => h.trim()),
        descriptions: formData.descriptions.filter((d) => d.trim()),
        final_urls: [{ url: formData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
        path1: formData.path1 || undefined,
        path2: formData.path2 || undefined,
      };

      // Optimistically update the campaign-from-cache query
      queryClient.setQueryData<Campaign>(['campaign-from-cache', campaignId], (oldData) => {
        if (!oldData) {
          debug.log('CreateRSAWizard: No oldData for campaign-from-cache');
          return oldData;
        }
        debug.log('CreateRSAWizard: Updating campaign-from-cache', {
          campaignId,
          selectedAdGroupId,
          adGroupsCount: oldData.ads_group_list?.length,
        });
        const updated = {
          ...oldData,
          ads_group_list: oldData.ads_group_list?.map((ag) => {
            if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
              debug.log('CreateRSAWizard: Found matching ad group, adding ad');
              return {
                ...ag,
                ads: [...(ag.ads || []), optimisticAd],
                ads_list: [...(ag.ads_list || []), optimisticAd],
              };
            }
            return ag;
          }),
        } as Campaign;
        return updated;
      });

      // Also update app-config cache (this is the source of truth for useCampaignFromCache)
      debug.log('CreateRSAWizard: Updating app-config cache', { memberUuid, campaignId });
      if (memberUuid) {
        queryClient.setQueryData<AppConfiguration>(['app-config', memberUuid], (oldConfig) => {
          if (!oldConfig?.configuration?.campaign_list) {
            debug.log('CreateRSAWizard: No oldConfig for app-config');
            return oldConfig;
          }
          debug.log('CreateRSAWizard: Found app-config, updating campaign_list');
          return {
            ...oldConfig,
            configuration: {
              ...oldConfig.configuration,
              campaign_list: oldConfig.configuration.campaign_list.map((camp) => {
                if (String(camp.campaign_id) === String(campaignId)) {
                  debug.log('CreateRSAWizard: Found campaign in app-config, updating ads_group_list');
                  return {
                    ...camp,
                    ads_group_list: camp.ads_group_list?.map((ag) => {
                      if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
                        debug.log('CreateRSAWizard: Found ad group in app-config, adding ad');
                        return {
                          ...ag,
                          ads: [...(ag.ads || []), optimisticAd],
                          ads_list: [...(ag.ads_list || []), optimisticAd],
                        };
                      }
                      return ag;
                    }),
                  };
                }
                return camp;
              }),
            },
          };
        });
      }

      debug.log('CreateRSAWizard: Optimistically updated caches');

      // DON'T invalidate app-config - that would trigger a refetch from Google Ads which
      // may not have propagated the new ad yet, overwriting our optimistic update.
      // Instead, just invalidate campaign-from-cache to trigger a re-derive from the
      // already-updated app-config cache.
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
    } catch (error: unknown) {
      debug.error('CreateRSAWizard: Failed to create RSA', error);

      let errorMessage = 'Failed to create search ad. Please try again.';

      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;

        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Responsive Search Ad</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a search ad to <span className="font-medium">{campaignName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={wizardSteps.length}
            steps={wizardSteps}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {/* Step 1: Select Ad Group */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Ad Group</h3>
                <p className="text-sm text-gray-600">
                  Choose which ad group to add the new search ad to.
                </p>
              </div>

              <div className="space-y-3">
                {adGroups.map((ag) => {
                  const agId = ag.ad_group_id || ag.ads_group_id || '';
                  const agName = ag.ad_group_name || ag.ads_group_name || 'Unnamed Ad Group';
                  const isSelected = selectedAdGroupId === agId;

                  return (
                    <label
                      key={agId}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="adGroup"
                        value={agId}
                        checked={isSelected}
                        onChange={(e) => setSelectedAdGroupId(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">{agName}</span>
                    </label>
                  );
                })}
              </div>

              {errors.adGroup && (
                <p className="text-sm text-red-600">{errors.adGroup}</p>
              )}
            </div>
          )}

          {/* Step 2: Search Ad Creation */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Search Ad</h3>
                <p className="text-sm text-gray-600">
                  Create a Responsive Search Ad with headlines and descriptions.
                </p>
              </div>

              {/* Ad Type Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-gray-900">Responsive Search Ad</span>
                    <p className="text-xs text-gray-500">Google automatically optimizes headline and description combinations for best performance</p>
                  </div>
                </div>
              </div>

              {/* RSA Form */}
              <div className="space-y-6 pt-4 border-t border-gray-200">
                {/* Final URL */}
                <div>
                  <label htmlFor="final-url" className="block text-sm font-medium text-gray-700 mb-2">
                    Final URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    id="final-url"
                    value={formData.finalUrl}
                    onChange={(e) => setFormData((prev) => ({ ...prev, finalUrl: e.target.value }))}
                    placeholder="https://www.example.com/landing-page"
                    className={cn(
                      'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                      { 'border-red-500': errors.finalUrl }
                    )}
                  />
                  {errors.finalUrl && <p className="mt-1 text-sm text-red-600">{errors.finalUrl}</p>}
                  <p className="mt-1 text-xs text-gray-500">The landing page URL where users will go</p>
                </div>

                {/* Path Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="path1" className="block text-sm font-medium text-gray-700 mb-2">
                      Path 1 (optional)
                    </label>
                    <input
                      type="text"
                      id="path1"
                      value={formData.path1}
                      onChange={(e) => setFormData((prev) => ({ ...prev, path1: e.target.value }))}
                      placeholder="deals"
                      maxLength={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Display path (15 chars max)</p>
                  </div>
                  <div>
                    <label htmlFor="path2" className="block text-sm font-medium text-gray-700 mb-2">
                      Path 2 (optional)
                    </label>
                    <input
                      type="text"
                      id="path2"
                      value={formData.path2}
                      onChange={(e) => setFormData((prev) => ({ ...prev, path2: e.target.value }))}
                      placeholder="shoes"
                      maxLength={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Display path (15 chars max)</p>
                  </div>
                </div>

                {/* Headlines */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Headlines <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500">
                      {formData.headlines.length}/15 (min 3 required)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {formData.headlines.map((headline, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={headline}
                          onChange={(e) => handleHeadlineChange(index, e.target.value)}
                          placeholder={`Headline ${index + 1}`}
                          maxLength={30}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-500 self-center min-w-[40px]">
                          {headline.length}/30
                        </span>
                        {formData.headlines.length > 3 && (
                          <button
                            type="button"
                            onClick={() => removeHeadline(index)}
                            className="text-red-600 hover:text-red-700 px-2"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.headlines && <p className="mt-1 text-sm text-red-600">{errors.headlines}</p>}
                  {formData.headlines.length < 15 && (
                    <button
                      type="button"
                      onClick={addHeadline}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      + Add headline
                    </button>
                  )}
                </div>

                {/* Descriptions */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Descriptions <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500">
                      {formData.descriptions.length}/4 (min 2 required)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {formData.descriptions.map((description, index) => (
                      <div key={index} className="flex gap-2">
                        <textarea
                          value={description}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          placeholder={`Description ${index + 1}`}
                          maxLength={90}
                          rows={2}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-500 self-start mt-2 min-w-[40px]">
                          {description.length}/90
                        </span>
                        {formData.descriptions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeDescription(index)}
                            className="text-red-600 hover:text-red-700 px-2 self-start mt-2"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.descriptions && (
                    <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>
                  )}
                  {formData.descriptions.length < 4 && (
                    <button
                      type="button"
                      onClick={addDescription}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      + Add description
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Review Your Search Ad</h3>

              {/* Ad Group Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Target Ad Group</h4>
                <p className="text-sm text-gray-900">{selectedAdGroupName}</p>
              </div>

              {/* URL Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Landing Page</h4>
                <p className="text-sm text-gray-900 break-all">{formData.finalUrl}</p>
                {(formData.path1 || formData.path2) && (
                  <p className="text-sm text-gray-500 mt-1">
                    Display path: /{formData.path1}{formData.path2 ? `/${formData.path2}` : ''}
                  </p>
                )}
              </div>

              {/* Headlines Summary */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Headlines ({formData.headlines.filter(h => h.trim()).length})
                </h4>
                <ul className="list-disc list-inside text-sm text-gray-900 space-y-1">
                  {formData.headlines.filter(h => h.trim()).map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>

              {/* Descriptions Summary */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Descriptions ({formData.descriptions.filter(d => d.trim()).length})
                </h4>
                <ul className="list-disc list-inside text-sm text-gray-900 space-y-1">
                  {formData.descriptions.filter(d => d.trim()).map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Step {currentStep} of {wizardSteps.length}
            </div>

            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}

              {currentStep < wizardSteps.length ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Search Ad'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
